// StatementPDF.tsx
import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
} from '@react-pdf/renderer';
import {
  Box,
  Typography,
  Card,
  CardContent,
} from '@mui/material';
import StatementTable from '../components/transactions/StatementTable.tsx';
import DownloadButtons from '../components/transactions/DownloadButtons.tsx';
import * as ExcelJS from 'exceljs';

// Data structure
const statementData = [
  { date: '31-01-2022', voucher: 'JV/8855', narration: 'Disbursement', debit: '300,000.00', credit: '0.00', balance: '300,000.00' },
  { date: '31-01-2022', voucher: 'JV/8859', narration: 'Interest Capital', debit: '18,000.00', credit: '0.00', balance: '318,000.00' },
  { date: '31-03-2022', voucher: 'JV/9047', narration: 'Direct deposit for L/repay', debit: '0.00', credit: '43,000.00', balance: '293,000.00' },
  { date: '30-04-2023', voucher: 'JV/11168', narration: 'Interest Capital', debit: '30,000.00', credit: '0.00', balance: '184,000.00' },
  { date: '30-05-2023', voucher: 'JV/11305', narration: 'L/rep by swipe', debit: '0.00', credit: '84,000.00', balance: '100,000.00' },
  { date: '30-05-2023', voucher: 'JV/11307', narration: 'L/rep by swipe', debit: '0.00', credit: '133,000.00', balance: '-33,000.00' },
];

// Styles
const styles = StyleSheet.create({
  page: { padding: 20, fontSize: 10, fontFamily: 'Helvetica' },
  header: { fontSize: 14, fontWeight: 'bold', marginBottom: 10, textAlign: 'center' },
  section: { marginBottom: 10 },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderColor: '#ccc',
    paddingVertical: 2,
  },
  tableHeader: {
    backgroundColor: '#eee',
    fontWeight: 'bold',
  },
  col: {
    flex: 1,
    paddingHorizontal: 2,
  },
  colWide: {
    flex: 2,
    paddingHorizontal: 2,
  },
});

const StatementDocument = () => (
  <Document>
    <Page size="A4" style={styles.page}>
      <Text style={styles.header}>Statement of Account</Text>
      <View style={styles.section}>
        <Text>Name of Borrower: Mr. Daniel Bvurire</Text>
        <Text>Loan Number: 104219000314</Text>
        <Text>Statement Period: 01-01-2022 to 29-06-2023</Text>
        <Text>Sanctioned Amount: $300,000.00</Text>
        <Text>Disbursed Amount: $300,000.00</Text>
        <Text>Closing Balance: $-33,000.00</Text>
      </View>
      <View style={[styles.tableRow, styles.tableHeader]}>
        <Text style={styles.col}>Txn Date</Text>
        <Text style={styles.col}>Reference</Text>
        <Text style={styles.colWide}>Narration</Text>
        <Text style={styles.col}>Debit</Text>
        <Text style={styles.col}>Credit</Text>
        <Text style={styles.col}>Balance</Text>
      </View>
      {statementData.map((txn, idx) => (
        <View style={styles.tableRow} key={idx}>
          <Text style={styles.col}>{txn.date}</Text>
          <Text style={styles.col}>{txn.voucher}</Text>
          <Text style={styles.colWide}>{txn.narration}</Text>
          <Text style={styles.col}>{txn.debit}</Text>
          <Text style={styles.col}>{txn.credit}</Text>
          <Text style={styles.col}>{txn.balance}</Text>
        </View>
      ))}
    </Page>
  </Document>
);

function downloadCSV() {
  const header = ['Txn Date', 'Reference', 'Narration', 'Debit', 'Credit', 'Balance'];
  const rows = statementData.map(txn => [txn.date, txn.voucher, txn.narration, txn.debit, txn.credit, txn.balance]);
  const csvContent = [header, ...rows].map(e => e.map(v => `"${v}"`).join(',')).join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'statement.csv';
  a.click();
  URL.revokeObjectURL(url);
}

async function downloadExcel() {
  try {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Statement');
    
    // Add headers
    worksheet.addRow(['Txn Date', 'Reference', 'Narration', 'Debit', 'Credit', 'Balance']);
    
    // Add data rows
    statementData.forEach(txn => {
      worksheet.addRow([txn.date, txn.voucher, txn.narration, txn.debit, txn.credit, txn.balance]);
    });
    
    // Generate and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'statement.xlsx';
    a.click();
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error generating Excel:', error);
    alert('Error generating Excel file. Please try again.');
  }
}

function printTable() {
  const printContent = document.getElementById('statement-table');
  const printWindow = window.open('', '', 'width=900,height=700');
  if (printWindow && printContent) {
    printWindow.document.write('<html><head><title>Print Statement</title>');
    printWindow.document.write('</head><body >');
    printWindow.document.write(printContent.outerHTML);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  }
}

const StatementPdf: React.FC = () => (
  <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
    <Typography variant="h4" gutterBottom>Account Statement</Typography>
    
    <StatementTable data={statementData} />
    
    <Card>
      <CardContent>
        <DownloadButtons 
          data={statementData} 
          pdfDocument={<StatementDocument />} 
        />
      </CardContent>
    </Card>
  </Box>
);

export default StatementPdf;
